export function capitalizeFirstLetter(val: string) {
	if (!val) return "";
	return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

/**
 * Check if text contains only Latin characters (English alphabet)
 * @param text - Text to check
 * @returns true if text contains only Latin characters, numbers, spaces and common punctuation
 */
export function isLatinText(text: string): boolean {
	if (!text) return true;
	// Only Latin letters (a-z, A-Z), numbers, spaces, and common punctuation
	// Includes typographic quotes and apostrophes: ''""—–
	// Note: Inside character class [], most special chars lose meaning, but:
	// - ^ at start means negation, so we escape it: \^
	// - - must be escaped or at start/end: \-
	// - ] must be escaped: \]
	// - \ must be escaped: \\
	const latinRegex = /^[a-zA-Z0-9\s.,!?;:'"()[\]{}\-_/@#$%&*+=<>~`|\\'’""—–^]+$/;
	return latinRegex.test(text);
}

/**
 * Check if text contains any non-Latin characters
 * @param text - Text to check
 * @returns true if text contains non-Latin characters (e.g., Cyrillic, Chinese, etc.)
 */
export function hasNonLatinCharacters(text: string): boolean {
	return !isLatinText(text);
}

/**
 * Debug function to find all non-Latin characters in text
 * @param text - Text to analyze
 * @returns Array of objects with character details
 */
export function debugNonLatinCharacters(text: string): Array<{
	char: string;
	position: number;
	charCode: number;
	charCodeHex: string;
	context: string;
}> {
	if (!text) return [];

	const latinRegex = /[a-zA-Z0-9\s.,!?;:'"()[\]{}\-_/@#$%&*+=<>~`|\\'’""—–^]/;
	const results: Array<{
		char: string;
		position: number;
		charCode: number;
		charCodeHex: string;
		context: string;
	}> = [];

	for (let i = 0; i < text.length; i++) {
		const char = text[i];
		if (!latinRegex.test(char)) {
			const start = Math.max(0, i - 10);
			const end = Math.min(text.length, i + 10);
			results.push({
				char,
				position: i,
				charCode: char.charCodeAt(0),
				charCodeHex: char.charCodeAt(0).toString(16),
				context: text.substring(start, end).replace(/\n/g, "\\n").replace(/\r/g, "\\r")
			});
		}
	}

	return results;
}

export function validateEmailString(email: string): boolean {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
